/**
 * The bridge to the phone's own capabilities: picking videos, cutting them, sharing the result.
 *
 * Every function here works in BOTH worlds. Inside the Capacitor shell it calls the native
 * plugins (see mobile/android/.../*Plugin.java). In a plain phone browser at /m it falls back
 * to what the web can do, so the same screens stay testable without building an APK.
 */
import { isNativeApp } from "./native";

const plugin = (name) => {
  if (!isNativeApp()) return null;
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return (cap && cap.Plugins && cap.Plugins[name]) || null;
};

/** A native file path -> a URL the WebView can actually load. */
export const playableSrc = (path) => {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  if (cap && typeof cap.convertFileSrc === "function") return cap.convertFileSrc(path);
  return path;
};

export const canExport = () => !!plugin("ClipExport");
export const canTranscribe = () => !!plugin("Transcribe");
export const canShare = () => !!plugin("ShareFile");

/**
 * Pick videos from the gallery.
 * @returns {Promise<{cancelled: boolean, videos: Array}>} each video:
 *   { name, url (playable), path (native only), durationMs, width, height, file? (web only) }
 */
export async function pickVideos() {
  const picker = plugin("MediaPicker");
  if (picker) {
    const res = await picker.pickVideos();
    const videos = (res.videos || []).map((v) => ({
      name: v.name,
      path: v.path,
      url: playableSrc(v.path),
      durationMs: v.durationMs || 0,
      duration: (v.durationMs || 0) / 1000,
      width: v.width || 0,
      height: v.height || 0,
    }));
    return { cancelled: !!res.cancelled, videos };
  }
  // Web fallback: a file input, resolved by the caller (it owns the <input>).
  return { cancelled: false, videos: null };
}

/** Which of these copied videos are still on disk? On the web, assume they are. */
export async function filesExist(paths) {
  const picker = plugin("MediaPicker");
  if (!picker) return paths || [];
  try {
    const res = await picker.filesExist({ paths: paths || [] });
    return res.paths || [];
  } catch (e) {
    return [];
  }
}

/** Free the copies the native picker made. Best-effort; never throws. */
export async function clearImported() {
  const picker = plugin("MediaPicker");
  if (!picker) return;
  try { await picker.clearImported(); } catch (e) { /* not worth surfacing */ }
}

/**
 * Cut the picks out of their source videos and join them into ONE mp4, on the phone.
 * @param {Array<{video:string, sourceId:string, start:number, end:number}>} picks
 * @param {Object<string,string>} paths  source id -> native file path (project.paths)
 * @param {(fraction:number)=>void} onProgress
 * @returns {Promise<{path:string, durationMs:number, bytes:number}>}
 */
export async function exportPicks(picks, paths, onProgress) {
  const exporter = plugin("ClipExport");
  if (!exporter) throw new Error("no-exporter");

  let listener = null;
  if (onProgress && exporter.addListener) {
    listener = await exporter.addListener("exportProgress", (e) => {
      const f = e && typeof e.fraction === "number" ? e.fraction : 0;
      onProgress(Math.min(Math.max(f, 0), 1));
    });
  }
  try {
    return await exporter.exportClips({
      clips: picks.map((p) => ({
        // The encoder needs the real file, not the URL the WebView plays it from.
        uri: (paths && paths[p.sourceId]) || p.video,
        startMs: Math.round(p.start * 1000),
        endMs: Math.round(p.end * 1000),
      })),
    });
  } finally {
    if (listener && listener.remove) { try { await listener.remove(); } catch (e) { /* ignore */ } }
  }
}

/** Open the Android share sheet on a finished file. */
export async function shareVideo(path, text) {
  const share = plugin("ShareFile");
  if (!share) throw new Error("no-share");
  return share.shareVideo({ path, text, title: "Share clip" });
}

/**
 * Transcribe one video ON THE PHONE — no upload, no account, works in airplane mode.
 *
 * The native side decodes the audio track, resamples it to 16 kHz mono and runs it through a
 * bundled offline recogniser, which returns words with timings. Sentences are assembled up here
 * (`sentencesFromWords`) so the grouping rules stay testable in plain JS.
 *
 * @param {string} path native file path from the picker
 * @param {(fraction:number)=>void} onProgress
 * @returns {Promise<Array<{text:string,start:number,end:number}>>} words, seconds
 */
export async function transcribeVideo(path, onProgress) {
  const engine = plugin("Transcribe");
  if (!engine) throw new Error("no-transcriber");

  let listener = null;
  if (onProgress && engine.addListener) {
    listener = await engine.addListener("transcribeProgress", (e) => {
      const f = e && typeof e.fraction === "number" ? e.fraction : 0;
      onProgress(Math.min(Math.max(f, 0), 1));
    });
  }
  try {
    const res = await engine.transcribeVideo({ path });
    return (res.words || []).map((w) => ({
      text: String(w.text || ""),
      start: Number(w.start) || 0,
      end: Number(w.end) || 0,
    }));
  } finally {
    if (listener && listener.remove) { try { await listener.remove(); } catch (e) { /* ignore */ } }
  }
}

/**
 * Open a link OUTSIDE the app — the phone's browser for the website, the mail app for feedback.
 * In the shell a plain window.open stays inside the WebView (and Google blocks sign-in there),
 * so the native side fires an ACTION_VIEW intent. On the web it is an ordinary new tab.
 */
export async function openExternal(url) {
  const share = plugin("ShareFile");
  if (share && typeof share.openUrl === "function") {
    try { await share.openUrl({ url }); return true; } catch (e) { /* fall through */ }
  }
  try { window.open(url, "_blank", "noopener"); return true; } catch (e) { return false; }
}
